import React, { useRef, useState } from "react";
import "./browser_packing_list.css";
import { HomeOutlined, SearchOutlined } from '@ant-design/icons';
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import { Breadcrumb, Button, Input, Space, Switch, Table } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from 'swr';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { mapDbStatusToDisplayStatus } from "../../components/hoc_packing_list";
import { useAuth } from "../../context/AuthContext";


const BrowserPacking: React.FC = () => {
    const { userData } = useAuth();
    const [isprogress, setIsprogress] = useState(false);
    const [showAllWarehouse, setShowAllWarehouse] = useState(false);
    const hasOtoritas77 = userData?.otoritas ? userData.otoritas.split(';').map((val: string) => val.trim()).includes('77') : false;
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    interface DataType {
        id: number;
        key: string;
        packingListNo: string;
        packingListDate: string;
        salesOrderNo: string;
        customerName: string;
        orderStatus: string;
        deliveryStatus: string;
        warehouse?: string;
    }

    type DataIndex = keyof DataType;
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const fetcher = (url: string) => axios.get(url).then(res => res.data);
    const { data, error, isLoading } = useSWR<DataType[]>(`${backendUrl}/api/packing-lists-with-customer`, fetcher);
    const { data: warehouseData } = useSWR<{ warehouse_code: string; warehouse_name: string; }[]>(`${backendUrl}/api/warehouses`, fetcher);
    const warehouseMap = React.useMemo(() => {
        return new Map((warehouseData || []).map(w => [w.warehouse_code, w.warehouse_name]));
    }, [warehouseData]);
    const navigate = useNavigate();

    if (error) return <div>Gagal memuat daftar packing list.</div>;
    if (isLoading) return <div>Memuat daftar packing list...</div>;

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${String(dataIndex)}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            (record[dataIndex] ?? '')
                .toString()
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Packing List No',
            dataIndex: 'packingListNo',
            key: 'packingListNo',
            width: '15%',
            ...getColumnSearchProps('packingListNo'),
        },
        {
            title: 'Packing List Date',
            dataIndex: 'packingListDate',
            key: 'packingListDate',
            width: '15%',
            ...getColumnSearchProps('packingListDate'),
        },
        {
            title: 'Sales Order No',
            dataIndex: 'salesOrderNo',
            key: 'salesOrderNo',
            width: '15%',
            ...getColumnSearchProps('salesOrderNo'),
        },
        {
            title: 'Customer Name',
            dataIndex: 'customerName',
            key: 'customerName',
            width: '20%',
            ...getColumnSearchProps('customerName'),
        },
        {
            title: 'Order Status',
            dataIndex: 'orderStatus',
            key: 'orderStatus',
            width: '10%',
            filters: Array.from(
                new Set(
                    (data || [])
                        .map(item => item.orderStatus)
                        .filter((status): status is string => !!status)
                )
            ).map(status => ({ text: mapDbStatusToDisplayStatus(status), value: status })),
            onFilter: (value, record) => record.orderStatus === value,
            render: (text: string) => mapDbStatusToDisplayStatus(text),
        },
        {
            title: 'Delivery Status',
            dataIndex: 'deliveryStatus',
            key: 'deliveryStatus',
            width: '10%',
            ...getColumnSearchProps('deliveryStatus'),
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse',
            key: 'warehouse',
            width: '10%',
            filters: Array.from(
                new Set(
                    (data || [])
                        .map(item => item.warehouse)
                        .filter((wh): wh is string => !!wh)
                )
            ).map(wh => ({ text: warehouseMap.get(wh) || wh, value: wh })),
            onFilter: (value, record) => record.warehouse === value,
            sorter: (a, b) => {
                const nameA = warehouseMap.get(a.warehouse || '') || a.warehouse || '';
                const nameB = warehouseMap.get(b.warehouse || '') || b.warehouse || '';
                return nameA.localeCompare(nameB);
            },
            render: (warehouseCode: string) => warehouseMap.get(warehouseCode) || warehouseCode || '-',
        },
    ];

    const filteredData = (data || []).filter(item => {
        if (showAllWarehouse && hasOtoritas77) {
            return true;
        }
        return item.warehouse === userData?.warehouse_code;
    });

    return (
        <div id="i2bt" style={transformStyle}>
            <div className="gjs-grid-row" id="i2clq">
                <div className="gjs-grid-column" id="iuek8">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span>Data Packing List</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/packing_list",
                                title: (
                                    <>
                                        <span>Packing List</span>
                                    </>
                                ),
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="i0p2">
                <div className="gjs-grid-column" id="i7s6">
                    {hasOtoritas77 && (
                        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Switch checked={showAllWarehouse} onChange={setShowAllWarehouse} />
                            <span style={{ fontSize: '14px' }}>Show All Warehouse</span>
                        </div>
                    )}
                    <Table<DataType>
                        columns={columns}
                        dataSource={filteredData}
                        rowKey="id"
                        loading={isLoading || isprogress}
                        onRow={(record) => ({
                            onDoubleClick: () => {
                                if (isprogress) return;
                                setIsprogress(true);
                                navigate('/dashboard/packing_list', { state: { packingListNo: record.packingListNo } });
                            },
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserPacking;